import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VERIFY_BASE = typeof window !== "undefined" ? window.location.origin : BACKEND_URL;

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", { 
    day: "numeric", 
    month: "long", 
    year: "numeric" 
  });
};

const getLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

// Compose "Full Name (SHORT)" from an Institution / IPCB record. See the
// matching helper in IPCBReport.jsx for the rationale.
const formatOrgName = (org, fallback = "") => {
  if (!org) return fallback;
  const full = (org.name || "").trim();
  const short = (org.short_name || "").trim();
  if (full && short && !full.toUpperCase().includes(short.toUpperCase())) {
    return `${full} (${short})`;
  }
  return full || short || fallback;
};

// Generate evidence description - includes override remarks for audit trail
const getEvidence = (competency, aiRecommendation, scores, candidate) => {
  // First check if this competency has an override remark from scores
  const scoreEntry = scores?.find(s => s.competency_id === competency.competency_id);
  
  // Check if score has override info
  if (scoreEntry?.override_applied || scoreEntry?.assessor_notes?.startsWith('[OVERRIDE]')) {
    const overrideRemark = scoreEntry.assessor_notes?.replace('[OVERRIDE] ', '') || '';
    return `Verified via alternative assessment: ${overrideRemark}`;
  }
  
  // Check if candidate has admin_override with competency-specific remarks
  const adminOverride = candidate?.admin_override;
  if (adminOverride?.competency_overrides) {
    const overrideEntry = adminOverride.competency_overrides.find(
      o => o.competency_id === competency.competency_id
    );
    if (overrideEntry) {
      return `Verified via alternative assessment (Score: ${overrideEntry.override_score}/5): ${overrideEntry.remark}`;
    }
  }
  
  // Get AI analysis evidence
  const match = aiRecommendation?.competency_matches?.find(
    m => m.competency_id === competency.competency_id || m.unit_code === competency.unit_code
  );
  
  // Check evidence strength - keep wording concise for report presentation
  const strength = match?.evidence_strength?.toLowerCase() || '';
  
  // Extract a concise summary from evidence text (limit length for table readability)
  const truncateEvidence = (text, maxLen = 200) => {
    if (!text) return '';
    // Remove "Candidate needs to provide:" sections to keep it concise
    let clean = text.replace(/\.\s*Candidate needs to provide:.*$/i, '.');
    if (clean.length > maxLen) clean = clean.substring(0, maxLen).trim() + '...';
    return clean;
  };

  const rawEvidence = match?.evidence_provided || match?.evidence_summary || '';
  
  if (strength === 'no_evidence') {
    return `Insufficient evidence. ${truncateEvidence(rawEvidence) || 'Additional documentation required.'}`;
  }
  if (strength === 'weak') {
    return `Limited evidence. ${truncateEvidence(rawEvidence) || 'Additional documentation recommended.'}`;
  }
  if (strength === 'partial') {
    return `Partial evidence. ${truncateEvidence(rawEvidence) || 'Some supporting evidence found but not fully comprehensive.'}`;
  }
  
  if (rawEvidence) return truncateEvidence(rawEvidence, 300);
  return "Verified through portfolio documentation.";
};

// Main Institution Report - Professional Design with Large Fonts
export const InstitutionReport = forwardRef(({ 
  candidate, 
  institution, 
  competencies = [],
  scores = [],
  aiRecommendation,
  assessorName = "RPL Coordinator"
}, ref) => {
  const level = aiRecommendation?.recommended_level || candidate?.assessor_decision?.final_level || "diploma";
  const issueDate = formatDate(candidate?.updated_at || new Date().toISOString());
  const award = candidate?.final_award || aiRecommendation?.primary_award || "Pending Selection";
  
  const levelComps = competencies.filter(c => c.qualification_level === level);
  const totalCredits = levelComps.reduce((sum, c) => sum + (c.credit_hours || 3), 0);
  
  const getScore = (id) => scores.find(s => s.competency_id === id)?.assessor_score;
  const getCredits = (comp) => {
    const score = getScore(comp.competency_id);
    return (score === undefined || score >= 3) ? (comp.credit_hours || 3) : 0;
  };
  
  const earnedCredits = levelComps.reduce((sum, c) => sum + getCredits(c), 0);

  const pageStyle = {
    fontFamily: "Tahoma, Arial, sans-serif",
    fontSize: "12pt",
    lineHeight: "1.5",
    color: "#1a1a1a",
    padding: "40px 50px",
    minHeight: "1100px",
    backgroundColor: "white",
  };

  return (
    <div ref={ref} style={{ backgroundColor: "white" }} data-testid="institution-report">
      
      {/* PAGE 1 */}
      <div data-report-page="institution-1" style={{ ...pageStyle, pageBreakAfter: "always" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "4px solid #b45309", paddingBottom: "20px", marginBottom: "30px", position: "relative" }}>
          {candidate?.institution_report_id && (
            <div style={{
              position: "absolute", top: "-8px", right: "0",
              backgroundColor: "#b45309", color: "white",
              padding: "6px 14px", borderRadius: "0 0 0 8px",
              fontSize: "10pt", fontFamily: "Courier New, monospace",
              fontWeight: "bold", letterSpacing: "1px",
            }}>
              Report ID: {candidate.institution_report_id}
            </div>
          )}
          <div style={{ flex: 1, marginTop: candidate?.institution_report_id ? "24px" : "0" }}>
            <h1 style={{ fontSize: "18pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 6px 0" }}>
              FINAL RPL ASSESSMENT SUMMARY
            </h1>
            <p style={{ fontSize: "13pt", color: "#b45309", margin: "0 0 10px 0" }}>Official Certification Document</p>
            <p style={{ fontSize: "12pt", fontWeight: "600", color: "#333", margin: "0" }}>
              {formatOrgName(institution, "Awarding Institution")}
            </p>
          </div>
          {institution?.logo_url && (
            <img src={getLogoUrl(institution.logo_url)} alt={institution.name} crossOrigin="anonymous" style={{ height: "70px", width: "auto", maxWidth: "220px", objectFit: "contain", marginTop: candidate?.institution_report_id ? "24px" : "0" }} />
          )}
        </div>

        <p style={{ textAlign: "right", fontSize: "10pt", color: "#666", marginBottom: "15px" }}>Page 1 of 2</p>

        {/* Award Banner */}
        <div style={{ backgroundColor: "#fef3c7", border: "2px solid #b45309", borderRadius: "8px", padding: "20px", textAlign: "center", marginBottom: "25px" }}>
          <p style={{ fontSize: "10pt", fontWeight: "bold", color: "#b45309", margin: "0 0 8px 0", textTransform: "uppercase", letterSpacing: "1px" }}>APPROVED RPL AWARD</p>
          <p style={{ fontSize: "16pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 10px 0" }}>{award}</p>
          <p style={{ fontSize: "12pt", color: "#333", margin: "0" }}>
            {level.charAt(0).toUpperCase() + level.slice(1)} Level | {earnedCredits} Credits Awarded
          </p>
        </div>

        {/* Candidate Info */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px", marginBottom: "25px" }}>
          <div style={{ backgroundColor: "#f8fafc", padding: "12px", borderRadius: "6px" }}>
            <p style={{ fontSize: "9pt", fontWeight: "bold", color: "#666", margin: "0 0 3px 0", textTransform: "uppercase" }}>Candidate Name</p>
            <p style={{ fontSize: "12pt", color: "#1a1a1a", margin: "0" }}>{candidate?.full_name}</p>
          </div>
          <div style={{ backgroundColor: "#f8fafc", padding: "12px", borderRadius: "6px" }}>
            <p style={{ fontSize: "9pt", fontWeight: "bold", color: "#666", margin: "0 0 3px 0", textTransform: "uppercase" }}>RPL Coordinator</p>
            <p style={{ fontSize: "12pt", color: "#1a1a1a", margin: "0" }}>{assessorName}</p>
          </div>
          <div style={{ backgroundColor: "#f8fafc", padding: "12px", borderRadius: "6px" }}>
            <p style={{ fontSize: "9pt", fontWeight: "bold", color: "#666", margin: "0 0 3px 0", textTransform: "uppercase" }}>Date of Issuance</p>
            <p style={{ fontSize: "12pt", color: "#1a1a1a", margin: "0" }}>{issueDate}</p>
          </div>
        </div>

        {/* Competency Evidence Matrix */}
        <h2 style={{ fontSize: "14pt", fontWeight: "bold", color: "#1e3a5f", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px", marginBottom: "15px" }}>
          Competency Evidence Matrix
        </h2>
        
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11pt" }}>
          <thead>
            <tr style={{ backgroundColor: "#1e3a5f", color: "white" }}>
              <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "11%" }}>Unit Code</th>
              <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "18%" }}>Competency</th>
              <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "18%" }}>Evidence Required</th>
              <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "43%" }}>Evidence Provided</th>
              <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "center", width: "10%" }}>Credits</th>
            </tr>
          </thead>
          <tbody>
            {levelComps.length > 0 ? (
              levelComps.slice(0, 5).map((comp, i) => {
                const creditsReceived = getCredits(comp);
                return (
                  <tr key={comp.competency_id} style={{ backgroundColor: i % 2 === 0 ? "white" : "#f9fafb" }}>
                    <td style={{ border: "1px solid #e5e7eb", padding: "10px", color: "#2563eb", fontWeight: "600", fontSize: "10pt" }}>{comp.unit_code}</td>
                    <td style={{ border: "1px solid #e5e7eb", padding: "10px", fontWeight: "500", fontSize: "11pt" }}>{comp.title}</td>
                    <td style={{ border: "1px solid #e5e7eb", padding: "10px", fontSize: "10pt", color: "#4b5563" }}>{comp.evidence_required}</td>
                    <td style={{ border: "1px solid #e5e7eb", padding: "10px", fontSize: "10pt", color: "#4b5563", lineHeight: "1.5" }}>
                      {getEvidence(comp, aiRecommendation, scores, candidate)}
                    </td>
                    <td style={{ border: "1px solid #e5e7eb", padding: "12px", textAlign: "center", fontWeight: "bold" }}>
                      <span style={{ 
                        backgroundColor: creditsReceived > 0 ? "#dcfce7" : "#fee2e2", 
                        color: creditsReceived > 0 ? "#166534" : "#991b1b",
                        padding: "5px 12px",
                        borderRadius: "15px"
                      }}>
                        {creditsReceived}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} style={{ border: "1px solid #e5e7eb", padding: "25px", textAlign: "center", fontStyle: "italic", color: "#666" }}>
                  No competencies defined for {level} level
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Credits Summary */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "15px" }}>
          <div style={{ backgroundColor: "#eff6ff", border: "2px solid #2563eb", borderRadius: "8px", padding: "15px", textAlign: "right" }}>
            <p style={{ fontSize: "10pt", fontWeight: "bold", color: "#666", margin: "0 0 3px 0", textTransform: "uppercase" }}>Total Credits Awarded</p>
            <p style={{ fontSize: "20pt", fontWeight: "bold", color: "#1e3a5f", margin: "0" }}>
              {earnedCredits} <span style={{ fontSize: "12pt", fontWeight: "normal", color: "#666" }}>/ {totalCredits}</span>
            </p>
          </div>
        </div>
      </div>

      {/* PAGE 2 */}
      <div data-report-page="institution-2" style={pageStyle}>
        {/* Header */}
        <div style={{ borderBottom: "4px solid #b45309", paddingBottom: "12px", marginBottom: "15px" }}>
          <h1 style={{ fontSize: "16pt", fontWeight: "bold", color: "#1e3a5f", margin: "0" }}>FINAL RPL ASSESSMENT SUMMARY</h1>
        </div>
        <p style={{ textAlign: "right", fontSize: "10pt", color: "#666", marginBottom: "15px" }}>Page 2 of 2</p>

        {/* Continue table if more competencies */}
        {levelComps.length > 5 && (
          <>
            <h2 style={{ fontSize: "14pt", fontWeight: "bold", color: "#1e3a5f", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px", marginBottom: "15px" }}>
              Competency Evidence Matrix (Continued)
            </h2>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11pt", marginBottom: "30px" }}>
              <thead>
                <tr style={{ backgroundColor: "#1e3a5f", color: "white" }}>
                  <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "11%" }}>Unit Code</th>
                  <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "18%" }}>Competency</th>
                  <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "18%" }}>Evidence Required</th>
                  <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "left", width: "43%" }}>Evidence Provided</th>
                  <th style={{ border: "1px solid #1e3a5f", padding: "10px", textAlign: "center", width: "10%" }}>Credits</th>
                </tr>
              </thead>
              <tbody>
                {levelComps.slice(5).map((comp, i) => {
                  const creditsReceived = getCredits(comp);
                  return (
                    <tr key={comp.competency_id} style={{ backgroundColor: i % 2 === 0 ? "white" : "#f9fafb" }}>
                      <td style={{ border: "1px solid #e5e7eb", padding: "10px", color: "#2563eb", fontWeight: "600", fontSize: "10pt" }}>{comp.unit_code}</td>
                      <td style={{ border: "1px solid #e5e7eb", padding: "10px", fontWeight: "500", fontSize: "11pt" }}>{comp.title}</td>
                      <td style={{ border: "1px solid #e5e7eb", padding: "10px", fontSize: "10pt", color: "#4b5563" }}>{comp.evidence_required}</td>
                      <td style={{ border: "1px solid #e5e7eb", padding: "10px", fontSize: "10pt", color: "#4b5563", lineHeight: "1.5" }}>
                        {getEvidence(comp, aiRecommendation, scores, candidate)}
                      </td>
                      <td style={{ border: "1px solid #e5e7eb", padding: "12px", textAlign: "center", fontWeight: "bold" }}>
                        <span style={{ 
                          backgroundColor: creditsReceived > 0 ? "#dcfce7" : "#fee2e2", 
                          color: creditsReceived > 0 ? "#166534" : "#991b1b",
                          padding: "5px 12px",
                          borderRadius: "15px"
                        }}>
                          {creditsReceived}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {/* Alternative Assessment Verification Section - for audit trail */}
        {candidate?.admin_override && (
          <div style={{ backgroundColor: "#f0f9ff", border: "2px solid #3b82f6", borderRadius: "8px", padding: "15px", marginBottom: "20px" }}>
            <h3 style={{ fontSize: "13pt", fontWeight: "bold", color: "#1e40af", margin: "0 0 10px 0" }}>
              Alternative Assessment Verification Applied
            </h3>
            <div style={{ fontSize: "11pt", color: "#1e3a8a" }}>
              <p style={{ margin: "3px 0" }}><strong>Verified By:</strong> {candidate.admin_override.admin_name}</p>
              <p style={{ margin: "3px 0" }}><strong>Verification Date:</strong> {formatDate(candidate.admin_override.created_at)}</p>
              <p style={{ margin: "3px 0" }}><strong>Initial Evidence Coverage:</strong> {candidate.admin_override.evidence_coverage_at_override}%</p>
              <p style={{ margin: "8px 0 3px 0" }}><strong>Assessment Rationale:</strong></p>
              <p style={{ margin: "0", fontStyle: "italic", backgroundColor: "white", padding: "8px", borderRadius: "4px", border: "1px solid #bfdbfe", fontSize: "10pt" }}>
                {candidate.admin_override.justification}
              </p>
              {candidate.admin_override.competency_overrides?.length > 0 && (
                <>
                  <p style={{ margin: "10px 0 8px 0" }}><strong>Competency-Specific Alternative Assessments ({candidate.admin_override.competency_overrides.length}):</strong></p>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt", backgroundColor: "white", borderRadius: "4px", overflow: "hidden" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#dbeafe" }}>
                        <th style={{ border: "1px solid #bfdbfe", padding: "6px", textAlign: "left" }}>Unit Code</th>
                        <th style={{ border: "1px solid #bfdbfe", padding: "6px", textAlign: "left" }}>Competency</th>
                        <th style={{ border: "1px solid #bfdbfe", padding: "6px", textAlign: "center" }}>Score</th>
                        <th style={{ border: "1px solid #bfdbfe", padding: "6px", textAlign: "left" }}>Alternative Evidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidate.admin_override.competency_overrides.map((override, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "white" : "#f8fafc" }}>
                          <td style={{ border: "1px solid #bfdbfe", padding: "6px", color: "#2563eb", fontWeight: "500" }}>{override.unit_code}</td>
                          <td style={{ border: "1px solid #bfdbfe", padding: "6px" }}>{override.title}</td>
                          <td style={{ border: "1px solid #bfdbfe", padding: "6px", textAlign: "center", fontWeight: "bold" }}>{override.override_score}/5</td>
                          <td style={{ border: "1px solid #bfdbfe", padding: "6px", fontStyle: "italic" }}>{override.remark}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        {/* Certification Summary */}
        <h2 style={{ fontSize: "14pt", fontWeight: "bold", color: "#1e3a5f", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px", marginBottom: "15px" }}>
          Certification Summary
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" }}>
          <div style={{ backgroundColor: "#f8fafc", padding: "15px", borderRadius: "6px" }}>
            <p style={{ fontSize: "9pt", fontWeight: "bold", color: "#666", margin: "0 0 5px 0", textTransform: "uppercase" }}>Award Title</p>
            <p style={{ fontSize: "14pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 5px 0" }}>{award}</p>
            <p style={{ fontSize: "12pt", color: "#333", margin: "0" }}>Level: {level.charAt(0).toUpperCase() + level.slice(1)}</p>
          </div>
          <div style={{ backgroundColor: "#eff6ff", padding: "15px", borderRadius: "6px" }}>
            <p style={{ fontSize: "9pt", fontWeight: "bold", color: "#666", margin: "0 0 5px 0", textTransform: "uppercase" }}>Credits Awarded</p>
            <p style={{ fontSize: "22pt", fontWeight: "bold", color: "#1e3a5f", margin: "0 0 5px 0" }}>{earnedCredits}</p>
            <p style={{ fontSize: "12pt", color: "#333", margin: "0" }}>Out of {totalCredits} available</p>
          </div>
        </div>

        {/* Approval */}
        <div style={{ backgroundColor: "#f0fdf4", border: "2px solid #22c55e", borderRadius: "8px", padding: "15px", marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <p style={{ fontSize: "9pt", fontWeight: "bold", color: "#666", margin: "0 0 3px 0", textTransform: "uppercase" }}>Approved By</p>
              <p style={{ fontSize: "13pt", fontWeight: "600", color: "#1a1a1a", margin: "0" }}>RPL Certification Unit</p>
              <p style={{ fontSize: "11pt", color: "#666", margin: "3px 0 0 0" }}>{formatOrgName(institution, "Awarding Institution")}</p>
            </div>
            <span style={{ backgroundColor: "#166534", color: "white", padding: "8px 16px", borderRadius: "20px", fontSize: "11pt", fontWeight: "bold" }}>
              VERIFIED & APPROVED
            </span>
          </div>
        </div>

        {/* Authorization */}
        <h2 style={{ fontSize: "14pt", fontWeight: "bold", color: "#1e3a5f", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px", marginBottom: "15px" }}>
          Authorization
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "50px" }}>
          <div>
            <p style={{ fontSize: "10pt", fontWeight: "bold", color: "#666", marginBottom: "8px", textTransform: "uppercase" }}>Authorized Signature:</p>
            <div style={{ borderBottom: "2px solid #1a1a1a", height: "50px", marginBottom: "8px" }}></div>
            <p style={{ fontSize: "12pt", margin: "0" }}>{institution?.authorized_officer || "Authorized Officer"}</p>
            <p style={{ fontSize: "11pt", color: "#666", margin: "3px 0 0 0" }}>RPL Certification Unit</p>
          </div>
          <div>
            <p style={{ fontSize: "10pt", fontWeight: "bold", color: "#666", marginBottom: "8px", textTransform: "uppercase" }}>Official Stamp:</p>
            <div style={{ 
              width: "120px", 
              height: "80px", 
              border: "2px dashed #ccc", 
              borderRadius: "6px",
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center" 
            }}>
              <span style={{ fontSize: "11pt", color: "#999" }}>STAMP</span>
            </div>
          </div>
        </div>

        {/* Footer with QR Code for public verification */}
        <div style={{ borderTop: "2px solid #b45309", paddingTop: "15px", marginTop: "30px" }}>
          {candidate?.institution_report_id && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "24px", marginBottom: "15px" }}>
              <div style={{ background: "#ffffff", padding: "8px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                <QRCodeCanvas
                  value={`${VERIFY_BASE}/verify/${candidate.institution_report_id}`}
                  size={140}
                  level="H"
                  includeMargin={false}
                />
                <p style={{ fontSize: "9pt", color: "#666", margin: "6px 0 0 0", fontFamily: "Courier New, monospace", textAlign: "center" }}>
                  Scan to verify
                </p>
              </div>
              <div style={{ textAlign: "left", maxWidth: "320px" }}>
                <p style={{ fontSize: "11pt", color: "#1a1a1a", margin: "0 0 6px 0", fontWeight: "bold" }}>
                  Verify this credential:
                </p>
                <p style={{ fontSize: "10pt", color: "#475569", margin: "0", lineHeight: "1.5" }}>
                  Scan the QR code with any phone camera, or visit <strong>{VERIFY_BASE.replace(/^https?:\/\//, "")}/verify</strong> and enter the Report ID below.
                </p>
                <p style={{ fontSize: "12pt", color: "#1e3a5f", margin: "8px 0 0 0", fontFamily: "Courier New, monospace", fontWeight: "bold", letterSpacing: "1px" }}>
                  {candidate.institution_report_id}
                </p>
              </div>
            </div>
          )}
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: "11pt", margin: "0 0 5px 0" }}>
              This document certifies the successful completion of RPL assessment.
            </p>
            <p style={{ fontSize: "10pt", color: "#666", margin: "0", fontFamily: "Courier New, monospace" }}>
              Reference: {candidate?.institution_report_id || `RPL-${candidate?.candidate_id?.slice(-8)?.toUpperCase() || "XXXXXXXX"}`} | Issued: {issueDate}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

InstitutionReport.displayName = "InstitutionReport";
export default InstitutionReport;
